import { VerticalID as GraphQLVerticalID } from 'src/graphql'
import { QuantumThemeProviderProps } from '@sendoutcards/quantum-design-ui'
import { useMemo, useQueryParams } from '.'

const staticFile = (directory: string) => (filename: string) =>
  `${
    process.env.NODE_ENV !== 'development' ? '/static/soc-frontend' : ''
  }/${directory}/${filename}`

const sideImage = staticFile('SideImages')
const logo = staticFile('Logos')

type VerticalID =
  | 'soc'
  | 'blue42'
  | 'bella'
  | 'streamva'
  | 'streammrkt'
  | 'promptingu'
  | 'greenerstill'
  | 'bydesign'

type Vertical = {
  id: VerticalID
  graphqlVerticalID: GraphQLVerticalID
  name: string
  theme: QuantumThemeProviderProps['theme']
  backgroundSrc?: string
  logoSrc?: string
  dashLogoSrc?: string
  primaryColor?: string
  secondaryColor?: string
}

const clientIdForMarketshow: { [key: string]: VerticalID | undefined } = {
  '553': 'blue42',
  '554': 'streamva',
  '555': 'streammrkt',
  '556': 'promptingu',
  '557': 'bella',
}

const getVertical = (id: string | undefined): Vertical => {
  switch (id) {
    case 'blue42':
      return {
        id,
        graphqlVerticalID: GraphQLVerticalID.BLUE_42,
        name: 'Blue 42',
        theme: 'blue42',
        backgroundSrc: sideImage('Blue42SideImage.jpg'),
        logoSrc: logo('Blue42.jpg'),
        dashLogoSrc: logo('logo-transparent-blue42.png'),
        primaryColor: '#140fee',
        secondaryColor: '#35a4bc',
      }
    case 'bella':
      return {
        id,
        graphqlVerticalID: GraphQLVerticalID.BELLA_CADEAUX,
        name: 'Bella Cadeaux',
        theme: 'bellaCadeaux',
        backgroundSrc: sideImage('BellaCadeauxSideImage.png'),
        logoSrc: logo('BellaCadeaux.jpg'),
        dashLogoSrc: logo('logo-transparent-bella-cadeaux.png'),
        primaryColor: '#ce8e47',
        secondaryColor: '#525a59',
      }
    case 'streamva':
      return {
        id,
        graphqlVerticalID: GraphQLVerticalID.STREAM_VA,
        name: 'Stream VA',
        theme: 'streamVA',
        backgroundSrc: sideImage('StreamVASideImage.jpg'),
        logoSrc: logo('StreamVA.jpg'),
        dashLogoSrc: logo('logo-transparent-stream-va.png'),
        primaryColor: '#61dce8',
        secondaryColor: '#1f4177',
      }
    case 'streammrkt':
      return {
        id,
        graphqlVerticalID: GraphQLVerticalID.STREAM_MARKETING,
        name: 'Stream Marketing',
        theme: 'streamMarketing',
        backgroundSrc: sideImage('StreamMarketingSideImage.png'),
        logoSrc: logo('StreamMarketing.png'),
        dashLogoSrc: logo('logo-transparent-stream-marketing.png'),
        primaryColor: '#b38048',
        secondaryColor: '#314a51',
      }
    case 'promptingu':
      return {
        id,
        graphqlVerticalID: GraphQLVerticalID.PROMPTING_U,
        name: 'Prompting U',
        theme: 'promptingU',
        backgroundSrc: sideImage('PromptingUSideImage.jpg'),
        logoSrc: logo('PromptingU.png'),
        dashLogoSrc: logo('logo-transparent-promptingu.png'),
        primaryColor: '#1f4177',
        secondaryColor: '#a99a67',
      }
    case 'greenerstill':
      return {
        id,
        graphqlVerticalID: GraphQLVerticalID.GREENER_STILL,
        name: 'Greener Still',
        theme: 'greenerStill',
        logoSrc: logo('GreenerStill.png'),
        dashLogoSrc: logo('logo-transparent-gs.png'),
        primaryColor: '#74C468',
        secondaryColor: '#52C1D1',
      }
    case 'bydesign':
      return {
        id,
        graphqlVerticalID: GraphQLVerticalID.BYDESIGN,
        name: 'ByDesign',
        theme: undefined,
      }
    default:
      return {
        id: 'soc',
        graphqlVerticalID: GraphQLVerticalID.SENDOUTCARDS,
        name: 'SendOutCards',
        theme: undefined,
      }
  }
}

const useVertical = (): Vertical => {
  const { client, marketshow } = useQueryParams()
  const id = (marketshow && clientIdForMarketshow[marketshow]) || client
  return useMemo(() => getVertical(id), [id])
}

export default useVertical
